export const TOUR_PHASES = [
  "WHAT O.N.E.TECH IS",
  "LIVE BROKERAGE OPERATIONS",
  "FOCUSED CLIENT JOURNEY",
  "GOVERNANCE & HUMAN ESCALATION",
  "RETENTION INTELLIGENCE",
  "WHEN O.N.E.TECH DOES NOTHING",
  "OPERATIONAL IMPACT",
  "THE COMPLETE OPERATING MODEL",
];

export const TOUR_PIPELINE = [
  "CONNECTED BROKER SYSTEMS",
  "EVENT DETECTED",
  "CLIENT STATE + WHY",
  "POLICY / GOVERNANCE CHECK",
  "DECISION",
  "MULTILINGUAL ACTION / HUMAN ESCALATION / NO ACTION",
  "CLIENT RESPONSE",
  "STATE UPDATE",
  "ATTRIBUTION",
  "OPERATIONAL IMPACT",
];

export const TOUR_CHAINS = {
  ftd: [
    ["WHY PRIORITISED", "VIP 92/100 — high funding intent + live engagement"],
    ["NEXT BEST ACTION", "Senior sales contact · KYC/deposit assistance"],
    ["ASSISTED OUTCOME", "€2,500 deposit completed — FTD confirmed"],
    ["IMPACT", "Assisted Revenue attributed — visible in the evidence being highlighted"],
  ],
  "payments-esc": [
    ["WHY PRIORITISED", "High intent + critical action priority — repeated payment failures"],
    ["NEXT BEST ACTION", "Payment assistance · approved alternative payment route"],
    ["ASSISTED OUTCOME", "£8,000 deposit completed via bank transfer"],
    ["IMPACT", "Recovered deposit opportunity — human-assisted attribution"],
  ],
  retention: [
    ["WHY PRIORITISED", "RETENTION PRIORITY — engagement falling vs baseline"],
    ["NEXT BEST ACTION", "Relationship manager intervention"],
    ["ASSISTED OUTCOME", "Client re-engaged — login and activity resumed"],
    ["IMPACT", "Retained / recovered client value"],
  ],
  "hv-esc": [
    ["WHY PRIORITISED", "CRITICAL — high-value client, 14 days inactive"],
    ["NEXT BEST ACTION", "Relationship manager intervention"],
    ["ASSISTED OUTCOME", "Client re-engaged — watchlist reviewed"],
    ["IMPACT", "Retained / recovered client value"],
  ],
  withdrawal: [
    ["WHY PRIORITISED", "High-value client — sensitive regulated journey"],
    ["NEXT BEST ACTION", "Process per withdrawal policy · approved status communication"],
    ["GOVERNED OUTCOME", "Withdrawal processed correctly — human action"],
    ["IMPACT", "Operational outcome measured while governance controls remained active"],
  ],
  "withdrawal-pt": [
    ["WHY PRIORITISED", "Routine withdrawal status request — governed journey"],
    ["NEXT BEST ACTION", "Approved status communication only"],
    ["GOVERNED OUTCOME", "Withdrawal processing normally — client acknowledged"],
    ["IMPACT", "Operational outcome measured while governance controls remained active"],
  ],
};

export const TOUR_STEPS = [
  {
    phase: 1, route: "/", center: true,
    title: "O.N.E.Tech Brokerage Operational Intelligence Infrastructure",
    body: "O.N.E.Tech sits across the broker's existing CRM, trading platform, payments, KYC and communication systems. It continuously detects lifecycle events, determines where and why clients are stuck, checks whether action is permitted, triggers approved action or human escalation, and measures what happens next.",
    list: ["O.N.E.Tech does not replace the broker's existing stack."],
    next: "NEXT — SEE LIVE OPERATIONS",
  },
  {
    phase: 2, route: "/live", target: "connected-systems",
    title: "LIVE BROKERAGE OPERATIONS",
    body: "A real brokerage does not process one client at a time. Registrations, funding failures, KYC events, trading activity changes, retention signals and human tasks happen continuously and in parallel.",
    next: "NEXT",
  },
  {
    phase: 2, route: "/live", target: "event-stream",
    title: "LIVE BROKERAGE ACTIVITY",
    body: "This background event stream continues processing across the whole client book even while one client journey is being inspected.",
    next: "NEXT — FOLLOW ONE CLIENT",
  },
  {
    phase: 3, route: "/live", caseId: "ftd", target: "live-event-card",
    title: "CAUSE — €2,500 deposit attempt failed",
    body: "Maria Rossi attempted a €2,500 first deposit. Source: Payment Gateway.",
    next: "NEXT",
  },
  {
    phase: 3, route: "/live", caseId: "ftd", target: "live-state-card",
    title: "DETECTION — Funding Failure",
    body: "O.N.E.Tech detects the event and places the client into a precise operational state.",
    next: "NEXT",
  },
  {
    phase: 3, route: "/live", caseId: "ftd", target: "why-detected",
    title: "WHY — 3DS authentication abandoned",
    body: "O.N.E.Tech does not only identify that a client is at risk. It shows why the client entered that operational state.",
    next: "NEXT",
  },
  {
    phase: 3, route: "/live", caseId: "ftd", target: "policy-check",
    title: "POLICY — eligibility checks",
    body: "Before any action, the system checks consent, entity rules, communication limits, suppression status and approved workflow conditions.",
    next: "NEXT",
  },
  {
    phase: 3, route: "/live", caseId: "ftd", target: "decision-card",
    title: "DECISION — AUTOMATED FUNDING RECOVERY",
    body: "The approved workflow, priority and confidence are recorded with every decision.",
    next: "NEXT",
  },
  {
    phase: 3, route: "/live", caseId: "ftd", target: "action-card",
    title: "ACTION — WhatsApp → Italian",
    body: "The communication channel and language are selected within the approved operational workflow.",
    next: "NEXT",
  },
  {
    phase: 3, route: "/live", caseId: "ftd", target: "outcome-card",
    title: "OUTCOME — ASSISTED FTD €2,500",
    body: "Payment retry completed → €2,500 FTD confirmed. The resulting outcome is recorded and attributed only when the defined attribution conditions are met.",
    next: "NEXT — SEE GOVERNANCE",
  },
  {
    phase: 3.5, phaseLabel: "LIVE INTELLIGENCE", route: "/live", caseId: "ftd", replay: true, target: "intelligence-panel",
    title: "LIVE INTELLIGENCE — Dynamic Qualification in Real Time",
    body: "This is where O.N.E.Tech moves beyond traditional CRM scoring. The lead is not classified once and forgotten. As new signals arrive, O.N.E.Tech continuously re-evaluates the lead, changes its qualification when necessary, explains exactly why the score changed, and determines the next best action. Watch this panel as the simulation replays.",
    next: "NEXT",
  },
  {
    phase: 3.5, phaseLabel: "LIVE INTELLIGENCE", route: "/live", caseId: "ftd", target: "arrival-intelligence",
    title: "ARRIVAL INTELLIGENCE — the initial score is explainable",
    body: "Qualification starts the moment the lead arrives. O.N.E.Tech evaluates acquisition source, affiliate quality, campaign, GEO eligibility, device/context, timing, CRM history and broker-specific criteria to establish the initial priority. But that first score is not permanent — every new behavioural signal re-evaluates the lead in real time.",
    variants: {
      client: { title: "CLIENT CONTEXT — not a new lead", body: "For existing funded clients there is no arrival score. O.N.E.Tech instead establishes context from tenure, historical funding, activity baseline and relationship value — then live signals re-evaluate from there." },
      withdrawal: { title: "CLIENT CONTEXT — governance first", body: "Existing client context (tenure, relationship value, lifecycle stage) frames the journey; withdrawal and compliance governance always take precedence." },
    },
    next: "NEXT",
  },
  {
    phase: 3.5, phaseLabel: "LIVE INTELLIGENCE", route: "/live", caseId: "ftd", target: "intel-classification",
    title: "CLASSIFICATION — STANDARD / PRIORITY / VIP",
    body: "The classification can change dynamically as the lead behaves. A Standard lead can become Priority or VIP within minutes — and a seemingly strong lead can also be downgraded if intent disappears.",
    variants: {
      client: { title: "CLIENT STATUS — HEALTHY / WATCH / RETENTION PRIORITY / CRITICAL", body: "For funded clients, O.N.E.Tech tracks client-risk status rather than acquisition classification. Status moves dynamically with engagement signals — in both directions." },
      withdrawal: { title: "CLIENT STATUS — governance-first", body: "Status and priority move with the journey, but withdrawal and compliance governance always take precedence over commercial signals." },
      kyc: { title: "CLASSIFICATION — resilient under KYC friction", body: "A high-intent lead keeps or raises its classification while KYC friction is resolved. Compliance friction is not the same as low intent." },
    },
    next: "NEXT",
  },
  {
    phase: 3.5, phaseLabel: "LIVE INTELLIGENCE", route: "/live", caseId: "ftd", target: "intel-score",
    title: "INTENT / VALUE SCORE",
    body: "This score reflects the strength of the lead's current commercial intent and value signals. It reacts to events such as engagement, funding intent, KYC progression, source quality and communication behaviour — watch it move as the simulation replays. O.N.E.Tech also separates commercial intent, operational urgency, eligibility and risk — so one score never hides the reason a lead needs attention.",
    variants: {
      client: { title: "CLIENT VALUE / ENGAGEMENT", body: "For funded clients this tracks engagement and relationship value. It falls as activity declines and recovers when the client returns." },
      withdrawal: { title: "CLIENT VALUE — held steady", body: "A withdrawal request does not erase client value. The score reflects the relationship — urgency moves on the priority side instead." },
      payment: { body: "A failed payment does not automatically reduce lead quality — confirmed deposit intent keeps the score high even when the transaction fails." },
      kyc: { body: "KYC progression signals strengthen the score — document friction does not erase commercial intent." },
    },
    next: "NEXT",
  },
  {
    phase: 3.5, phaseLabel: "LIVE INTELLIGENCE", route: "/live", caseId: "ftd", target: "intel-priority",
    title: "ACTION PRIORITY",
    body: "Intent and urgency are not always the same thing. A lead can have high intent but become even more urgent because of a payment failure, KYC blockage or another conversion obstacle. O.N.E.Tech calculates both.",
    variants: {
      client: { title: "INTERVENTION PRIORITY", body: "For funded clients, urgency means intervention priority. It can rise sharply while engagement falls — a high-priority client is not necessarily a healthy one." },
      withdrawal: { title: "OPERATIONAL PRIORITY — governance-bound", body: "Operational priority rises for correct handling, but permitted actions stay inside withdrawal policy: status communication, authorised support and human escalation only." },
    },
    next: "NEXT",
  },
  {
    phase: 3.5, phaseLabel: "LIVE INTELLIGENCE", route: "/live", caseId: "ftd", target: "intel-rank",
    title: "PRIORITY RANK — not evaluated in isolation",
    body: "The system does not evaluate this lead in isolation. It continuously compares the active opportunity against the rest of the pipeline and decides who deserves attention first.",
    next: "NEXT",
  },
  {
    phase: 3.5, phaseLabel: "LIVE INTELLIGENCE", route: "/live", caseId: "ftd", target: "intel-why-now",
    title: "WHY THIS LEAD, WHY NOW?",
    body: "This combines commercial intent, urgency, operational context and availability into one explainable decision.",
    next: "NEXT",
  },
  {
    phase: 3.5, phaseLabel: "LIVE INTELLIGENCE", route: "/live", caseId: "ftd", target: "intel-reasons",
    title: "WHY IT CHANGED",
    body: "Every change is explainable. The system shows which signals caused the qualification to move — so sales, compliance and management can understand the decision rather than receiving a black-box score. The reasons shown are the actual signals from this scenario.",
    next: "NEXT",
  },
  {
    phase: 3.5, phaseLabel: "LIVE INTELLIGENCE", route: "/live", caseId: "ftd", target: "intel-nba",
    title: "NEXT BEST ACTION",
    body: "Classification alone is not the objective. O.N.E.Tech uses the same signals to decide what should happen next — automatically trigger the appropriate action or escalate to the right human when required. The recommendation shown is the live NBA for this scenario.",
    next: "NEXT",
  },
  {
    phase: 3.5, phaseLabel: "LIVE INTELLIGENCE", route: "/live", caseId: "ftd", target: "intel-crm-status",
    title: "CRM WRITE-BACK",
    body: "The intelligence is written back into the existing CRM. The CRM remains the system of record. O.N.E.Tech adds the decision layer: who matters now, why, how urgently, and what should happen next.",
    next: "NEXT",
  },
  {
    phase: 3.5, phaseLabel: "LIVE INTELLIGENCE", route: "/live", caseId: "ftd", target: "intelligence-panel", compare: true,
    title: "TRADITIONAL CRM vs O.N.E.TECH",
    body: "The difference is not another score. It is a continuously updated decision layer sitting above the existing stack.",
    next: "NEXT — SEE GOVERNANCE",
  },
  {
    phase: 4, route: "/live", caseId: "compliance", target: "live-state-card",
    title: "SENSITIVE KYC STATE",
    body: "Ahmed Al-Mansoori's case carries a sensitive compliance status.",
    next: "NEXT",
  },
  {
    phase: 4, route: "/live", caseId: "compliance", target: "automation-blocked",
    title: "AUTOMATION BLOCKED",
    body: "Not every operational event should be automated. This case has a sensitive compliance status requiring authorised human review.",
    next: "NEXT",
  },
  {
    phase: 4, route: "/live", caseId: "compliance", target: "action-card",
    title: "CRM TASK CREATED — Compliance Team",
    body: "O.N.E.Tech identifies and routes the case with an SLA timer, but the broker's authorised Compliance team retains decision authority.",
    next: "NEXT",
  },
  {
    phase: 4, route: "/live", caseId: "compliance", target: "policy-check",
    title: "GOVERNED BY POLICY",
    body: "Automation is permitted only where policy, permission and confidence conditions are satisfied.",
    next: "NEXT — SEE RETENTION INTELLIGENCE",
  },
  {
    phase: 5, route: "/live", caseId: "retention", target: "live-state-card",
    title: "RETENTION RISK — HIGH",
    body: "Luca Bianchi is a funded client showing early disengagement.",
    next: "NEXT",
  },
  {
    phase: 5, route: "/live", caseId: "retention", target: "why-detected",
    title: "WHY DETECTED",
    body: "O.N.E.Tech continuously evaluates behavioural changes across the funded lifecycle rather than waiting for a client to become fully dormant.",
    list: ["Login activity declined", "Deposit frequency declined", "Previously high-engagement client", "No open support issue"],
    next: "NEXT",
  },
  {
    phase: 5, route: "/live", caseId: "retention", target: "action-card",
    title: "PERSONALISED RE-ENGAGEMENT",
    body: "A governed, personalised re-engagement is triggered in the client's own language.",
    next: "NEXT",
  },
  {
    phase: 5, route: "/live", caseId: "retention", target: "outcome-card",
    title: "RE-ENGAGED — ASSISTED RE-ENGAGEMENT",
    body: "Client login detected → state updated automatically → the outcome is recorded as an assisted re-engagement.",
    next: "NEXT — SEE WHEN O.N.E.TECH DOES NOTHING",
  },
  {
    phase: 6, route: "/live", caseId: "suppressed", target: "live-event-card",
    title: "FTD OPPORTUNITY DETECTED",
    body: "Emma Johnson shows a clear commercial opportunity.",
    next: "NEXT",
  },
  {
    phase: 6, route: "/live", caseId: "suppressed", target: "policy-check",
    title: "POLICY CONDITION FAILED",
    body: "Communication permission unavailable — the eligibility check fails.",
    next: "NEXT",
  },
  {
    phase: 6, route: "/live", caseId: "suppressed", target: "automation-blocked",
    title: "SUPPRESSION RULE ACTIVE — NO ACTION",
    body: "Commercial opportunity does not override governance. If communication is not permitted, O.N.E.Tech does not send it.",
    next: "NEXT — SEE OPERATIONAL IMPACT",
  },
  {
    phase: 7, route: "/impact", target: "leakage-summary",
    title: "OPERATIONAL IMPACT",
    body: "O.N.E.Tech connects operational activity to measurable outcomes.",
    next: "NEXT",
  },
  {
    phase: 7, route: "/impact", target: "value-at-risk-total",
    title: "VALUE AT RISK",
    body: "Client and account value associated with unresolved lifecycle opportunities.",
    next: "NEXT",
  },
  {
    phase: 7, route: "/impact", target: "attribution-table", chain: true,
    title: "FROM DECISION TO MEASURED IMPACT",
    body: "Earlier, O.N.E.Tech identified why this lead required priority attention and selected the Next Best Action. Here we close the loop. The Impact layer shows what happened after that decision — including assisted conversion, recovered opportunity and attributable revenue.",
    finalNote: "O.N.E.Tech does not stop at recommending an action. It connects the original signal and decision to the resulting operational and commercial outcome — making the intelligence measurable.",
    next: "NEXT",
  },
  {
    phase: 7, route: "/impact", target: "attribution-breakdown",
    title: "FOUR-WAY OUTCOME ATTRIBUTION",
    list: [
      "ASSISTED — successful progression where an eligible O.N.E.Tech intervention occurred within the attribution rules",
      "HUMAN ASSISTED — O.N.E.Tech identified or routed the opportunity; the human team completed the intervention",
      "ORGANIC — the client progressed without an O.N.E.Tech intervention",
      "GOVERNANCE BLOCKS — opportunities where action was intentionally prevented by policy",
    ],
    next: "NEXT",
  },
  {
    phase: 7, route: "/impact", target: "impact-disclaimer",
    title: "SIMULATED PERFORMANCE DATA",
    body: "All performance data in this environment is simulated and demonstrates how production measurement and attribution would work. It is not proven broker performance.",
    next: "NEXT — SEE THE COMPLETE MODEL",
  },
  {
    phase: 8, route: "/", center: true, pipeline: true,
    title: "THE COMPLETE OPERATING MODEL",
    body: "O.N.E.Tech coordinates the operational signals between the systems the brokerage already uses, turning fragmented client events into governed action and measurable outcomes.",
    next: "COMPLETE TOUR",
  },
  {
    phase: 8, route: "/", center: true, final: true,
    title: "GUIDED TOUR COMPLETE",
    body: "You've seen how O.N.E.Tech detects, governs, acts and measures across a simulated brokerage environment. You can now explore the platform freely.",
  },
];
